import { Button } from '@native-instruments/webex-ni';
import { useState } from 'react';
import { generatePath, useLoaderData, useRouteLoaderData } from 'react-router';
import env from '~/env';
import type { ProductVariant } from '~/graphql';
import { ROUTE_PATHS } from '~/helpers/app-routes';
import { useIntl } from '~/helpers/intl';
import { isNotNullish, isTruthy } from '~/helpers/predicate';
import { getIsShippable, getStockStatus } from '~/helpers/product';
import { pushDataEvent } from '~/helpers/tracking';
import type {
  ProductInterface,
  loader,
} from '~/routes/$language.pricing.$slug/route';

type Props = {
  product: ProductInterface;
  variant?: ProductVariant | null;
  quantity?: number | null;
};

export function AddToCartButton({
  product,
  variant,
  quantity = 1,
  ...props
}: Props) {
  const { addToCartSku, user } = useLoaderData<typeof loader>();
  const { t } = useIntl();
  const root = useRouteLoaderData('root');
  const [isLoading, setLoading] = useState(false);

  const stockStatus = getStockStatus({ variant, product });
  const isOutOfStock = stockStatus === 'OUT_OF_STOCK';

  if (!addToCartSku || quantity == null) {
    throw new Error(
      `Cannot add to cart because SKU or quantity are empty. SKU: ${addToCartSku}, Quantity: ${quantity}`,
    );
  }

  const handleSubmit = () => {
    setLoading(true);

    const searchParams = new URLSearchParams({
      product: addToCartSku,
      qty: quantity.toString(),
    });

    if (product?.__typename === 'BundleProduct') {
      const bundleItemsUids = (
        product?.bundleItems
          ?.map((item) => item?.options?.map((option) => option?.uid)[0])
          .filter(isNotNullish) as string[]
      ).filter(isNotNullish) as string[];

      searchParams.set('selectedOptions', bundleItemsUids.join(','));
    }

    pushDataEvent({
      event: 'pdp_add_to_cart_click',
      objectID: product.sku,
      nativeId: user?.nativeId,
    });

    // We need to use window.location and force a new reload since we want to redirect
    // from the client side to a different app (ni v1 in this case).
    window.location.href = `${env.APP_BASE_URL}${generatePath(ROUTE_PATHS.cartV1, { language: root.language })}?${searchParams.toString()}`;
  };

  const shouldRedirectToJPStore =
    getIsShippable({ product, variant }) && root.country === 'JP';

  if (shouldRedirectToJPStore) {
    return (
      <a
        href={constructJPStoreLink(product.sku)}
        className="webex-btn webex-btn--primary webex-btn--sm w-full"
        data-testid={`media-integration-button-${addToCartSku}`}
      >
        {t('pricing.actions.buy', {
          fallback: 'Buy online at MediaIntegration',
        })}
      </a>
    );
  }

  return (
    <Button
      size="small"
      fullWidth
      onClick={() => {
        handleSubmit();
      }}
      status={isOutOfStock ? 'disabled' : isLoading ? 'loading' : 'active'}
      variant="primary"
      {...props}
    >
      {t('components.QuickAddToCartButton.add', {
        fallback: 'Add to cart',
      })}
    </Button>
  );
}

function constructJPStoreLink(sku?: string | null) {
  if (!isTruthy(sku)) return;

  //@TODO [WWW-12033] Temporary solution until field is implemented in Magento
  const jpStoreLink =
    jpStoreLinks[sku as keyof typeof jpStoreLinks]?.jp_store_link ??
    'https://store.minet.jp/category/_NI/';

  const utmTags =
    '?utm_source=native-instruments.com&utm_medium=referral&utm_campaign=NI+SITE';

  return `${jpStoreLink}${utmTags}`;
}

const jpStoreLinks = {
  KOMPLETEKEYBOARDS88MK2: {
    title: 'KOMPLETE KONTROL S88',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0010.html',
  },
  MASCHINEPlus: {
    title: 'MASCHINE+',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0016.html',
  },
  KOMPLETEKEYBOARDS49Mk2: {
    title: 'KOMPLETE KONTROL S49',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0011.html',
  },
  KOMPLETEKONTROLM32: {
    title: 'KOMPLETE KONTROL M32',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0007.html',
  },
  KOMPLETEKEYBOARDS61Mk2: {
    title: 'KOMPLETE KONTROL S61',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0012.html',
  },
  KOMPLETEAUDIO6MK2: {
    title: 'KOMPLETE AUDIO 6',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0001.html',
  },
  MASCHINEMk3: {
    title: 'MASCHINE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0014.html',
  },
  KOMPLETEKEYBOARDA61: {
    title: 'KOMPLETE KONTROL A61',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0006.html',
  },
  MASCHINEMikroMk3: {
    title: 'MASCHINE MIKRO',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0015.html',
  },
  KOMPLETEKEYBOARDA25: {
    title: 'KOMPLETE KONTROL A25',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0004.html',
  },
  KOMPLETEKEYBOARDA49: {
    title: 'KOMPLETE KONTROL A49',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0005.html',
  },
  KOMPLETEAUDIO1: {
    title: 'KOMPLETE AUDIO 1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0002.html',
  },
  KOMPLETEAUDIO2: {
    title: 'KOMPLETE AUDIO 2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0003.html',
  },
  TRAKTORKONTROLS2Mk3: {
    title: 'TRAKTOR KONTROL S2 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0018.html',
  },
  TRAKTORKONTROLX1MK3: {
    title: 'TRAKTOR X1 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0034.html',
  },
  ControlVinylsMk2: {
    title: 'TRAKTOR SCRATCH CONTROL VINYL BLACK MK2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0027.html',
  },
  TRAKTORSCRATCHControlDiscsMK2: {
    title: 'TRAKTOR SCRATCH CONTROL CD MK2',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0032.html',
  },
  TRAKTORKONTROLF1: {
    title: 'TRAKTOR KONTROL F1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0017.html',
  },
  TRAKTORKONTROLZ1: {
    title: 'TRAKTOR KONTROL Z1',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0023.html',
  },
  TRAKTORKONTROLS3: {
    title: 'TRAKTOR KONTROL S3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0019.html',
  },
  TRAKTORKONTROLS4Mk3: {
    title: 'TRAKTOR KONTROL S4 MK3',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0020.html',
  },
  NIPOWERSUPPLY40W: {
    title: 'NI POWER SUPPLY (40 W)',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0033.html',
  },
  TRAKTORDJCable: {
    title: 'TRAKTOR DJ CABLE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0025.html',
  },
  BUTTERRUGS: {
    title: 'TRAKTOR BUTTER RUG SLIPMATS',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0026.html',
  },
  TraktorKontrolBag: {
    title: 'TRAKTOR KONTROL BAG',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0024.html',
  },
  TRAKTORKONTROLS4MK3FLIGHTCASE: {
    title: 'TRAKTOR KONTROL S4 MK3 FLIGHTCASE',
    jp_store_link: 'https://store.minet.jp/category/_NI/NIHW0021.html',
  },
};
